import { type GenerateDeepLinkParams } from "@src/appV2/DeepLinks";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";

const STRIPE_DEEP_LINK_TIMEOUT_IN_MS = 2_500;

export interface GenerateStripeReturnUrlRequest {
  flow:
    | "legacy-onboarding"
    | "payout-settings-express-sign-in"
    | "payout-settings-onboarding"
    | "stripe-setup-page";
  generateDeepLink: (params: GenerateDeepLinkParams) => Promise<string>;
  link: string;
  purpose: "refresh-url" | "return-url";
  skipPreview: boolean;
  timeoutMs?: number;
}

export async function generateStripeReturnUrl(
  request: GenerateStripeReturnUrlRequest
): Promise<string> {
  const {
    flow,
    generateDeepLink,
    link,
    purpose,
    skipPreview,
    timeoutMs = STRIPE_DEEP_LINK_TIMEOUT_IN_MS,
  } = request;

  try {
    return await withTimeout({
      promise: generateDeepLink({ link, skipPreview }),
      timeoutMs,
    });
  } catch (error) {
    logError(APP_V2_APP_EVENTS.STRIPE_DEEP_LINK_FALLBACK_USED, {
      error,
      metadata: {
        flow,
        path: getPathname(link),
        purpose,
        reason: error instanceof StripeDeepLinkTimeoutError ? "timeout" : "error",
      },
    });

    return link;
  }
}

function getPathname(link: string): string {
  try {
    return new URL(link).pathname;
  } catch {
    return "[invalid-link]";
  }
}

async function withTimeout<T>({
  promise,
  timeoutMs,
}: {
  promise: Promise<T>;
  timeoutMs: number;
}): Promise<T> {
  if (timeoutMs <= 0) {
    return await promise;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new StripeDeepLinkTimeoutError(timeoutMs));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

class StripeDeepLinkTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Timed out waiting ${timeoutMs}ms for Stripe deep link generation`);
    this.name = "StripeDeepLinkTimeoutError";
  }
}
